import { IS_AUTO_TEST_HIDE, IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import {
    preOverquotaDebugLogger,
    preOverquotaExpGroup,
    preOverquotaModalConfig,
    preOverquotaModalIgnoreShowLimits,
    preQuotaEnablePaid,
    preQuotaSizeLimit,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaPre';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { getAccountDwhChains } from 'reactApp/modules/dwh/dwh.helpers';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { addPromoToShowQueue, promoShown, removePromo } from 'reactApp/modules/promo/promo.module';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserStorageActions } from 'reactApp/modules/user/userStorage';
import { loadUserQuotaSuccess } from 'reactApp/modules/userQuota/userQuota.module';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { getQuotaPromoUrl } from 'reactApp/sections/QuotaLanding/QuotaLanding.data';
import { store as reduxStore } from 'reactApp/store';
import { renderPreOverquotaModal } from 'reactApp/ui/PreOverquotaModal/PreOverquotaModal.helpers';
import { type ConditionsWithData, checkConditions } from 'reactApp/utils/conditionChecker';
import { sendDwh } from 'reactApp/utils/ga';
import { getPlatformInfo } from 'reactApp/utils/gaHelpers';
import { opener } from 'reactApp/utils/opener';
import { put, take } from 'redux-saga/effects';
import { call, select } from 'typed-redux-saga';

const PROMO_ID = 'preoverquota-promo';

const sendPreOverquotaModalDwh = ({
    action,
    mode,
}: {
    action: 'show' | 'click' | 'click-cleaner' | 'click-other' | 'close';
    mode?: 'landing' | 'paywall' | 'cleaner' | 'none';
}) => {
    const { dwhData: accountDwhData, dwhChains: accountDwhChains } =
        action === 'show' ? getAccountDwhChains(['name']) : { dwhData: {}, dwhChains: [] };

    const dwhData = {
        name: 'mnz_aon_preoverquota',
        platform: getPlatformInfo().join('_'),
        group: preOverquotaExpGroup,
        mode,
        ...accountDwhData,
    };

    const baseDwhChains: (keyof typeof dwhData)[][] = [['name', 'platform']];
    const expDwhChains: (keyof typeof dwhData)[][] = preOverquotaExpGroup ? [['name', 'group', 'platform']] : [];

    const dwh_enabled_fields = [...baseDwhChains, ...expDwhChains, ...accountDwhChains];

    sendDwh({
        eventCategory: 'modal',
        action,
        dwh_enabled: true,
        dwh_enabled_fields,
        dwhData,
    });
};

export function* checkPromoConditions() {
    const storage = yield* select(getCurrentStorage);
    const { isHome } = getStorage(storage);
    const isBizUser = yield* select(UserSelectors.isBizUser);
    const isCorp = yield* select(UserSelectors.isCorpUser);
    const isPaidUser = yield* select(UserSelectors.isPaidUser);

    // Форс-включение промки для отдельных автотестов.
    const force = yield put(UserStorageActions.get(`force_${PROMO_ID}`)) || false;

    let showCount: number = 0;

    const preOverquotaModalConditions: ConditionsWithData = {
        isHome,
        isNotBiz: !isBizUser,
        isNotOnPrem: !IS_ONPREMISE,
        isNotCorp: !isCorp,
        isNotPaidCheck: { check: preQuotaEnablePaid || !isPaidUser, preQuotaEnablePaid, isPaidUser },

        isNotAutotestHide: Boolean(!IS_AUTO_TEST_HIDE || force),
        preOverquotaModal: preOverquotaModalConfig.enable,
        isNotShown: () => {
            showCount = storeHelper.getValue(PROMO_ID) || 0;
            const isShown = showCount >= preOverquotaModalConfig.maxShowCount && !preOverquotaModalIgnoreShowLimits;

            return {
                check: !isShown,
                showCount,
                maxShowCount: preOverquotaModalConfig.maxShowCount,
                preOverquotaModalIgnoreShowLimits,
                PROMO_ID,
            };
        },
        isPeriodPassed: () => {
            const isPeriodPassed =
                preOverquotaModalIgnoreShowLimits ||
                !showCount ||
                storeHelper.isPassed(PROMO_ID, { daysCount: preOverquotaModalConfig.period });

            return {
                check: isPeriodPassed,
                lasTime: new Date(storeHelper.getLastTime(PROMO_ID)).toLocaleString(),
                now: new Date().toLocaleString(),
                preOverquotaModalIgnoreShowLimits,
                preOverquotaModalPeriod: preOverquotaModalConfig.period,
            };
        },
    };

    const { result: checkResult, resolvedConditions: overquotaModalResolvedConditions } = checkConditions(preOverquotaModalConditions);

    preOverquotaDebugLogger.info('preOverquotaPromo static check', overquotaModalResolvedConditions);

    if (!checkResult) {
        return false;
    }

    const isQuotaLoaded = yield* select(UserQuotaSelectors.isQuotaLoaded);
    if (!isQuotaLoaded) {
        yield take(loadUserQuotaSuccess);
    }

    const isOverquota = yield* select(UserQuotaSelectors.isOverquota);
    const { used: usedPercent } = yield* select(UserQuotaSelectors.getFilledPercents);

    preOverquotaDebugLogger.info('preOverquotaPromo dynamic check', {
        isNotOverquota: !isOverquota,
        overLimitCheck: { used: usedPercent, preQuotaOfferLimit: preQuotaSizeLimit, check: usedPercent >= preQuotaSizeLimit },
    });

    return !isOverquota && usedPercent >= preQuotaSizeLimit;
}

export function* initPreOverquotaPromo() {
    if (!(yield checkPromoConditions())) {
        return;
    }

    const showCount: number = storeHelper.getValue(PROMO_ID) || 0;

    yield put(
        addPromoToShowQueue({
            type: EPromoType.preOverquotaPromo,
            promoId: PROMO_ID,
            onShow: () => {
                reduxStore.dispatch(promoShown(EPromoType.preOverquotaPromo));
                storeHelper.markAsShown(PROMO_ID, showCount + 1);
                sendPreOverquotaModalDwh({ action: 'show' });
            },
            primaryButtonText: preOverquotaModalConfig.primaryButton?.enable ? preOverquotaModalConfig.primaryButton?.name : '',
            onClick: () => {
                reduxStore.dispatch(removePromo(EPromoType.preOverquotaPromo));
                sendPreOverquotaModalDwh({ action: 'click', mode: preOverquotaModalConfig.primaryButton?.mode });

                if (preOverquotaModalConfig.primaryButton?.mode === 'landing') {
                    reduxStore.dispatch(
                        historyPush({
                            id: '/promo/quota',
                            search: `?${PaymentUTM.preOverquotaModal}`,
                            hash: 'tariffs',
                        })
                    );
                    return;
                }
            },
            secondaryButtonText: (preOverquotaModalConfig.secondButton?.enable && preOverquotaModalConfig.secondButton?.name) || '',
            onSecondaryClick: () => {
                reduxStore.dispatch(removePromo(EPromoType.preOverquotaPromo));

                if (preOverquotaModalConfig.secondButton?.enable && preOverquotaModalConfig.secondButton?.mode === 'cleaner') {
                    sendPreOverquotaModalDwh({ action: 'click-cleaner' });

                    opener(
                        getQuotaPromoUrl({
                            query: `?${PaymentUTM.preOverquotaModalCleaner}`,
                            hash: 'cleaner',
                        })
                    );
                    return;
                }

                sendPreOverquotaModalDwh({ action: 'click-other', mode: preOverquotaModalConfig.secondButton?.mode });
            },
            onClose: () => {
                reduxStore.dispatch(removePromo(EPromoType.preOverquotaPromo));
                sendPreOverquotaModalDwh({ action: 'close' });
            },
            onDismiss: () => {
                reduxStore.dispatch(removePromo(EPromoType.preOverquotaPromo));
            },
        })
    );
}

export function* showPreOverquotaPromo() {
    const promo = yield* select(PromoSelectors.getPromo(EPromoType.preOverquotaPromo));

    if (!promo) {
        return;
    }

    const { onShow, onClick, onClose, onSecondaryClick, primaryButtonText, secondaryButtonText } = promo;

    yield* call(renderPreOverquotaModal, {
        isPrequota: true,
        primaryButtonText,
        secondaryButtonText,
        onShow,
        onClick,
        onDismiss: onClose,
        onSecondaryClick,
    });
}
